<template>
  <aside
    class="bg-surface-0 dark:bg-surface-900 flex flex-col h-full min-h-[400px]"
    data-test-id="merchant-recent-alerts"
  >
    <header
      class="flex items-center justify-between gap-3 px-5 pt-4 pb-3 border-b border-surface-200/70 dark:border-surface-700/70"
    >
      <h4
        class="text-sm font-semibold text-surface-700 dark:text-surface-200 uppercase tracking-wide"
      >
        Recent Alerts
      </h4>
      <Button
        label="View all"
        icon="pi pi-arrow-right"
        iconPos="right"
        size="small"
        variant="text"
        @click="$emit('switch-tab', 'alerts')"
        data-test-id="overview-view-all-alerts-btn"
      />
    </header>

    <ul
      v-if="recentAlerts.length > 0"
      class="flex flex-col gap-1.5 p-3 overflow-y-auto flex-1 min-h-0"
    >
      <li
        v-for="alert in recentAlerts"
        :key="alert.ALERT_ID"
        class="group relative border border-surface-200 dark:border-surface-700/70 rounded-md bg-surface-0 dark:bg-surface-900 hover:border-surface-300 dark:hover:border-surface-600 hover:shadow-sm transition-all cursor-pointer"
        :class="{ 'opacity-70 hover:opacity-100': alert.ACTIONED }"
        @click="viewAlertDetails(alert)"
        :data-test-id="`recent-alert-${alert.ALERT_ID}`"
      >
        <div class="flex flex-col gap-1.5 p-3">
          <!-- Line 1: severity pill + event type + stat value (right) -->
          <div class="flex items-center gap-2.5 min-w-0">
            <BadgeFccSeverity
              :value="formatTolerance(alert.TOLERANCE)"
              :background-color="
                getToleranceColor(alert.TOLERANCE).backgroundColor
              "
              :text-color="getToleranceColor(alert.TOLERANCE).textColor"
              :border-color="getToleranceColor(alert.TOLERANCE).borderColor"
              class="!px-1.5 !py-0.5 !text-[10px] flex-shrink-0"
            />
            <span
              class="text-sm font-medium text-surface-900 dark:text-surface-0 truncate flex-1 group-hover:underline underline-offset-[3px] decoration-primary-500 dark:decoration-primary-400"
              :title="formatEventType(alert.EVENT_TYPE)"
              :data-test-id="`alert-title-${alert.ALERT_ID}`"
            >
              {{ formatEventType(alert.EVENT_TYPE) }}
            </span>
            <span
              class="text-xs font-semibold px-1.5 py-0.5 rounded tabular-nums flex-shrink-0"
              :class="[
                getSignificanceColor(alert.SIGNIFICANCE).textColor,
                getSignificanceColor(alert.SIGNIFICANCE).backgroundColor,
              ]"
              :title="`Significance ${formatSignificance(alert.SIGNIFICANCE)}`"
            >
              {{ formatAlertType(alert.STATISTIC, alert.EVENT_TYPE) }}
            </span>
          </div>

          <!-- Line 2: meta strip + inline action -->
          <div
            class="flex items-center justify-between gap-3 text-[11px] text-surface-500 dark:text-surface-400"
          >
            <div class="flex items-center gap-3 min-w-0">
              <span
                class="inline-flex items-center gap-1 tabular-nums whitespace-nowrap"
                :title="formatDateTimeForDisplay(alert.TIMESTAMP)"
              >
                <i class="pi pi-clock text-[10px]" />
                {{ formatRelativeTime(alert.TIMESTAMP) }}
              </span>
              <button
                v-if="alert.CASE_ID"
                type="button"
                class="inline-flex items-center gap-1 text-primary-600 dark:text-primary-400 hover:underline underline-offset-[3px] font-medium tabular-nums"
                @click.stop="navigateToCaseDetails(alert.CASE_ID)"
                :data-test-id="`alert-case-link-${alert.CASE_ID}`"
              >
                <i class="pi pi-briefcase text-[10px]" />
                Case #{{ alert.CASE_ID }}
              </button>
              <span
                v-if="alert.ACTIONED"
                class="inline-flex items-center gap-1 text-green-700 dark:text-green-400 font-medium"
              >
                <i class="pi pi-check-circle text-[10px]" />
                Actioned
              </span>
              <span
                v-else-if="canEdit"
                class="inline-flex items-center gap-1 text-amber-700 dark:text-amber-300 font-medium"
              >
                <span class="w-1 h-1 rounded-full bg-amber-500" />
                Pending
              </span>
            </div>
            <Button
              v-if="canEdit && !alert.ACTIONED"
              label="Take Action"
              icon="pi pi-bolt"
              size="small"
              variant="text"
              class="!py-0.5 !text-xs opacity-0 group-hover:opacity-100 focus-visible:opacity-100 transition-opacity"
              @click.stop="openActionDrawer([alert])"
              :data-test-id="`alert-action-btn-${alert.ALERT_ID}`"
            />
          </div>
        </div>
      </li>
    </ul>

    <div
      v-else
      class="flex-1 flex flex-col items-center justify-center gap-2 px-4 py-12 text-center"
    >
      <div
        class="w-10 h-10 rounded-full bg-surface-100 dark:bg-surface-800 flex items-center justify-center"
      >
        <i
          class="pi pi-shield text-lg text-surface-400 dark:text-surface-500"
        />
      </div>
      <p class="text-sm font-medium text-surface-700 dark:text-surface-200">
        No active alerts.
      </p>
      <p class="text-xs text-surface-500 dark:text-surface-400 max-w-[260px]">
        Alerts surface here when merchant activity crosses a tolerance.
      </p>
    </div>

    <AlertActionDrawer
      v-if="canEdit"
      v-model:visible="actionDrawerVisible"
      :selected-alerts="selectedAlertsForAction"
      @action-submitted="handleActionSubmitted"
    />

    <AlertDetails
      v-model:visible="alertDetailsVisible"
      :alert="selectedAlert"
    />
  </aside>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { Button } from 'primevue'
import { useRouter } from 'vue-router'
import BadgeFccSeverity from '@/components/common/badges/BadgeFccSeverity.vue'
import AlertActionDrawer from '@/components/fcc/alerts/AlertActionDrawer.vue'
import AlertDetails from '@/components/fcc/alerts/AlertDetails.vue'
import { useFCCAlertStore } from '@/stores/fcc/fccAlertStore'
import { useFCCFormatting } from '@/util/fcc/useFCCFormatting'
import { usePerms } from '@/composables/usePerms'
import { PERMISSIONS } from '@/config/permissions'
import type { Alert } from '@/types/fcc'

const RECENT_ALERTS_LIMIT = 5

const emit = defineEmits<{
  'switch-tab': [tab: string]
  'action-completed': [count: number]
}>()

const router = useRouter()
const alertStore = useFCCAlertStore()
const { hasPerms } = usePerms()
const canEdit = computed(() => hasPerms(PERMISSIONS.FCC_UPDATE))

const {
  formatEventType,
  formatAlertType,
  formatSignificance,
  formatTolerance,
  formatDateTimeForDisplay,
  formatRelativeTime,
  getToleranceColor,
  getSignificanceColor,
} = useFCCFormatting()

const sortedAlerts = computed((): Alert[] =>
  [...alertStore.alerts].sort(
    (a, b) => new Date(b.TIMESTAMP).getTime() - new Date(a.TIMESTAMP).getTime(),
  ),
)
const recentAlerts = computed(() =>
  sortedAlerts.value.slice(0, RECENT_ALERTS_LIMIT),
)

const alertDetailsVisible = ref(false)
const selectedAlert = ref<Alert | null>(null)
const actionDrawerVisible = ref(false)
const selectedAlertsForAction = ref<Alert[]>([])

function viewAlertDetails(alert: Alert) {
  selectedAlert.value = alert
  alertDetailsVisible.value = true
}

function navigateToCaseDetails(caseId: number) {
  router.push({ name: 'fccCaseDetails', params: { id: caseId.toString() } })
}

function openActionDrawer(alerts: Alert[]) {
  selectedAlertsForAction.value = alerts
  actionDrawerVisible.value = true
}

function handleActionSubmitted() {
  const count = selectedAlertsForAction.value.length
  actionDrawerVisible.value = false
  selectedAlertsForAction.value = []
  emit('action-completed', count)
}
</script>
